import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicApi } from '../data/api';


const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'DEL', '0', 'Enter'] as const;

export function HomePage() {
  const navigate = useNavigate();
  const [athleteId, setAthleteId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setError(null);

    if (!/^\d{6}$/.test(athleteId)) {
      setError('Enter all six digits of your athlete ID.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await publicApi.confirmAthleteId(athleteId);
      if (!result) {
        setError('We could not find a public result for that athlete ID.');
        return;
      }
      void navigate(`/athletes/${encodeURIComponent(result.athleteId)}`);
    } catch {
      setError('Results are temporarily unavailable. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-gradient flex min-h-[calc(100dvh-3.5rem)] items-stretch justify-center p-3 sm:p-6">
      <div className="surface-card flex min-h-0 w-full max-w-[390px] flex-col overflow-hidden px-7 pb-4 pt-4">
        <div className="mx-auto flex w-full flex-1 flex-col justify-center text-center">
          {/* <img src={awsQsPartnerLogo} alt="AWS QuickSight Partner Logo" className="mx-auto mb-4" /> */}
          <h1 className="bg-gradient-to-r from-qs-navy via-qs-indigo to-qs-violet bg-clip-text text-[clamp(1.5rem,4dvh,2.25rem)] font-black leading-tight text-transparent">
            Patriots Creator Combine
          </h1>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.24em] text-qs-indigo">
            Presented by AWS
          </p>
          <p className="mt-[clamp(0.25rem,1dvh,0.75rem)] text-[clamp(0.75rem,1.8dvh,0.95rem)] font-bold text-aws-slate">
            Enter your Athlete ID to see your results
          </p>
          <div
            className="hero-gradient mt-[clamp(1rem,4dvh,2rem)] flex h-[clamp(3rem,8dvh,4rem)] items-center justify-center gap-[clamp(0.75rem,3vw,1.25rem)] rounded-xl text-[clamp(1.5rem,4dvh,2.25rem)] font-black text-white shadow-lg shadow-indigo-950/20"
            role="status"
            aria-label={`${athleteId.length} of 6 athlete ID digits entered`}
          >
            {Array.from({ length: 6 }, (_, index) => (
              <span key={index} aria-hidden="true">{athleteId[index] ?? '·'}</span>
            ))}
          </div>
          <div
            className="mt-[clamp(0.75rem,2dvh,1rem)] grid grid-cols-3 gap-[clamp(0.5rem,1.5dvh,0.75rem)]"
            aria-label="Athlete ID keypad"
          >
            {keys.map((key) => {
              const isEnter = key === 'Enter';
              const disabled = isLoading || (isEnter && athleteId.length !== 6);
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  aria-label={key === 'DEL' ? 'Delete last digit' : key}
                  onClick={() => {
                    setError(null);
                    if (key === 'DEL') {
                      setAthleteId((value) => value.slice(0, -1));
                    } else if (key === 'Enter') {
                      void handleSubmit();
                    } else {
                      setAthleteId((value) => (value.length < 6 ? `${value}${key}` : value));
                    }
                  }}
                  className={`grid h-[clamp(3rem,8dvh,4.5rem)] place-items-center rounded-xl border border-indigo-100 font-black shadow-sm transition enabled:hover:-translate-y-0.5 enabled:hover:border-qs-indigo enabled:hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 ${
                    isEnter ? 'button-primary border-0' : key === 'DEL' ? 'bg-qs-lilac text-qs-indigo' : 'bg-white text-qs-navy'
                  } ${
                    /^\d$/.test(key) ? 'text-[clamp(1.5rem,4dvh,2.5rem)]' : 'text-xs'
                  }`}
                >
                  {isEnter && isLoading ? 'Finding...' : key}
                </button>
              );
            })}
          </div>
          <div className="mt-3 min-h-12">
            {error ? (
              <p id="lookup-error" role="alert" className="min-h-12 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs font-bold text-rose-800">
                {error}
              </p>
            ) : (
              <div className="min-h-12 bg-transparent" aria-hidden="true" />
            )}
          </div>
        </div>
        <p className="pt-2 text-center text-[10px] font-medium">
          <Link to="/privacy-policy" className="text-aws-slate underline underline-offset-2 hover:text-qs-indigo">
            Privacy policy
          </Link>
        </p>
      </div>
    </div>
  );
}
